import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import socket from '../services/socket';
import '../styles/AdminMacroGame.css';
import AdminLEDButtons from './AdminLEDButtons';

const GAME_NAME = 'Dead Cat Bounce';
const GAME_ICON = '⛳';
const GAME_DESCRIPTION = 'Team relay putting with escalating obstacles';

const LEVELS = [
  { id: 1, name: 'Bull Market', par: 3, obstacles: 'None', description: 'Easy - No obstacles' },
  { id: 2, name: 'Normal Volatility', par: 6, obstacles: 'Windmill + Treadmill', description: 'Windmill and treadmill activate' },
  { id: 3, name: 'Market Chaos', par: 8, obstacles: 'Everything!', description: 'All obstacles + ramps + spinners' }
];

const PAYOUTS = [
  { place: '1st', amount: 10000, perPerson: true },
  { place: '2nd', amount: 6000, perPerson: true },
  { place: '3rd', amount: 3000, perPerson: true },
  { place: '4th-6th', amount: 1000, perPerson: true },
  { place: '7th+', amount: -2000, perPerson: true }
];

const AdminMinigolf = () => {
  const [gameActive, setGameActive] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [tradingPaused, setTradingPaused] = useState(false);
  const [teams, setTeams] = useState([]);
  const [currentTeamIndex, setCurrentTeamIndex] = useState(0);
  const [message, setMessage] = useState({ text: '', type: '' });

  const getAdminToken = () => localStorage.getItem('adminKey') || '';

  // Sync state to server
  const syncToServer = useCallback((active, level, teamsList) => {
    axios.post(`${API_BASE_URL}/api/admin/minigolf/update-state`, {
      active,
      currentLevel: level,
      teams: teamsList
    }, {
      headers: { 'x-admin-token': getAdminToken() }
    }).catch(err => console.error('Failed to sync minigolf state:', err));
  }, []);

  // Restore state from server on mount
  useEffect(() => {
    const fetchState = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/minigolf/state`);
        if (response.data.success && response.data.state) {
          const s = response.data.state;
          if (s.active) {
            setGameActive(true);
            setCurrentLevel(s.currentLevel || 1);
            setTeams(s.teams || []);
          }
        }
      } catch (error) {
        console.error('Failed to fetch minigolf state:', error);
      }
    };
    fetchState();
  }, []);

  // Socket listener
  useEffect(() => {
    const handleMarketUpdate = (data) => {
      setTradingPaused(!data.tradingEnabled);
    };

    socket.on('market-state-update', handleMarketUpdate);

    return () => {
      socket.off('market-state-update', handleMarketUpdate);
    };
  }, []);

  // Fetch market state on mount
  useEffect(() => {
    const fetchMarketState = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/admin/market/status`);
        setTradingPaused(!response.data.tradingEnabled);
      } catch (error) {
        console.error('Failed to fetch market state:', error);
      }
    };
    fetchMarketState();
  }, []);

  const startGame = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/big-game/announce`, {
        gameName: GAME_NAME,
        announcement: `${GAME_ICON} ${GAME_NAME} is starting! Trading is paused. Form your teams!`,
        pauseTrading: true
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });

      // Broadcast sprite intro to sideboards
      await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
        type: 'sprite:dead-cat',
        duration: 10,
        eventName: 'DEAD CAT BOUNCE!'
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });

      setGameActive(true);
      setCurrentLevel(1);
      setTradingPaused(true);
      syncToServer(true, 1, teams);
      setMessage({ text: 'Game started! Trading paused.', type: 'success' });
    } catch (error) {
      setMessage({ text: `Error: ${error.message}`, type: 'error' });
    }
  };

  const endGame = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/trading/resume`, {}, {
        headers: { 'x-admin-token': getAdminToken() }
      });

      await axios.post(`${API_BASE_URL}/api/admin/big-game/announce`, {
        gameName: `${GAME_NAME} - END`,
        announcement: `${GAME_ICON} ${GAME_NAME} has concluded! Trading resumes.`,
        pauseTrading: false
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });

      // Broadcast to sideboards
      await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
        type: 'minigolf:end',
        duration: 3,
        eventName: 'GAME OVER'
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });

      syncToServer(false, 1, []);
      setGameActive(false);
      setCurrentLevel(1);
      setTradingPaused(false);
      setTeams([]);
      setMessage({ text: 'Game ended! Trading resumed.', type: 'success' });
    } catch (error) {
      setMessage({ text: `Error: ${error.message}`, type: 'error' });
    }
  };

  const activateLevel = async (level) => {
    setCurrentLevel(level);
    syncToServer(gameActive, level, teams);
    socket.emit('macro-game-phase', {
      game: GAME_NAME,
      phase: `level${level}`,
      phaseName: LEVELS[level - 1].name,
      obstacles: LEVELS[level - 1].obstacles
    });

    // Broadcast level change to sideboards
    try {
      await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
        type: 'minigolf:level',
        level: level,
        duration: 3,
        eventName: `LEVEL ${level}!`
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });
    } catch (error) {
      console.error('Failed to broadcast level change:', error);
    }

    setMessage({ text: `Level ${level}: ${LEVELS[level - 1].name} activated!`, type: 'success' });
  };

  const triggerHoleInOne = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
        type: 'minigolf:hole-in-one',
        duration: 5,
        eventName: 'HOLE IN ONE!'
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });
      setMessage({ text: 'HOLE IN ONE! Animation triggered!', type: 'success' });
    } catch (error) {
      setMessage({ text: `Error: ${error.message}`, type: 'error' });
    }
  };

  const triggerScore = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
        type: 'minigolf:score',
        duration: 2,
        eventName: 'SCORE!'
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });
    } catch (error) {
      console.error('Failed to broadcast score:', error);
    }
  };

  const addTeam = () => {
    const teamNumber = teams.length + 1;
    const newTeams = [...teams, {
      id: teamNumber,
      name: `Team ${teamNumber}`,
      scores: [null, null, null],
      total: 0
    }];
    setTeams(newTeams);
    syncToServer(gameActive, currentLevel, newTeams);
  };

  const updateScore = (teamId, level, score) => {
    const updated = teams.map(t => {
      if (t.id === teamId) {
        const newScores = [...t.scores];
        newScores[level - 1] = score;
        const total = newScores.reduce((sum, s) => sum + (s || 0), 0);
        return { ...t, scores: newScores, total };
      }
      return t;
    });
    setTeams(updated);
    syncToServer(gameActive, currentLevel, updated);
  };

  const nextTeam = () => {
    if (currentTeamIndex < teams.length - 1) {
      setCurrentTeamIndex(currentTeamIndex + 1);
    } else {
      setCurrentTeamIndex(0);
    }
  };

  const generatePayoutQR = async (amount, reason) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/generate-qr`, {
        type: amount >= 0 ? 'bonus' : 'penalty',
        amount: amount,
        reason: `${GAME_NAME}: ${reason}`
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });

      window.open(`/qr/${response.data.qrCode}`, '_blank', 'width=400,height=500');
      setMessage({ text: `QR generated: ${reason}`, type: 'success' });
    } catch (error) {
      setMessage({ text: `Error generating QR: ${error.message}`, type: 'error' });
    }
  };

  const getSortedTeams = () => {
    return [...teams].sort((a, b) => a.total - b.total); // Lower is better
  };

  const renameTeam = (teamId, newName) => {
    const updated = teams.map(t =>
      t.id === teamId ? {...t, name: newName} : t
    );
    setTeams(updated);
    syncToServer(gameActive, currentLevel, updated);
  };

  return (
    <div className="admin-macro-game">
      <div className="game-header">
        <h2>{GAME_ICON} {GAME_NAME}</h2>
        <p className="game-description">{GAME_DESCRIPTION}</p>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      {/* Game Status */}
      <div className="status-bar">
        <div className={`status-item ${gameActive ? 'active' : 'inactive'}`}>
          <span className="status-label">Game:</span>
          <span className="status-value">{gameActive ? 'ACTIVE' : 'NOT STARTED'}</span>
        </div>
        <div className={`status-item ${tradingPaused ? 'paused' : 'active'}`}>
          <span className="status-label">Trading:</span>
          <span className="status-value">{tradingPaused ? 'PAUSED' : 'ACTIVE'}</span>
        </div>
        {gameActive && (
          <div className="status-item level">
            <span className="status-label">Level:</span>
            <span className="status-value">{currentLevel}/5</span>
          </div>
        )}
      </div>

      {/* Main Controls */}
      <div className="control-section">
        <h3>Game Controls</h3>
        <div className="button-row">
          {!gameActive ? (
            <button className="btn primary large" onClick={startGame}>
              Start {GAME_NAME}
            </button>
          ) : (
            <button className="btn danger large" onClick={endGame}>
              End Game
            </button>
          )}
        </div>
      </div>

      {/* Level Controls */}
      {gameActive && (
        <div className="level-section">
          <h3>Obstacle Levels</h3>
          <p className="level-note">Click to activate each level's obstacles</p>
          <div className="level-grid">
            {LEVELS.map(level => (
              <div
                key={level.id}
                className={`level-card ${currentLevel === level.id ? 'active' : ''} ${currentLevel > level.id ? 'completed' : ''}`}
                onClick={() => activateLevel(level.id)}
              >
                <div className="level-header">
                  <span className="level-number">Level {level.id}</span>
                  <span className="level-par">Par {level.par}</span>
                </div>
                <div className="level-name">{level.name}</div>
                <div className="level-obstacles">{level.obstacles}</div>
                <div className="level-desc">{level.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team Management & Scoring */}
      {gameActive && (
        <div className="teams-section">
          <h3>Teams & Scoring</h3>
          <button className="btn" onClick={addTeam} disabled={teams.length >= 8}>
            Add Team
          </button>

          {teams.length > 0 && (
            <div className="current-team-display">
              <span>Current: </span>
              <strong>{teams[currentTeamIndex]?.name}</strong>
              <button className="btn small" onClick={nextTeam}>Next Team</button>
            </div>
          )}

          <div className="scoring-table">
            <table>
              <thead>
                <tr>
                  <th>Team</th>
                  {LEVELS.map(l => <th key={l.id}>L{l.id}</th>)}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {getSortedTeams().map((team, rank) => (
                  <tr key={team.id} className={rank === 0 ? 'leader' : ''}>
                    <td>
                      <input
                        type="text"
                        value={team.name}
                        onChange={(e) => renameTeam(team.id, e.target.value)}
                        className="team-name-input"
                      />
                    </td>
                    {LEVELS.map(level => (
                      <td key={level.id}>
                        <input
                          type="number"
                          min="1"
                          max="20"
                          value={team.scores[level.id - 1] || ''}
                          onChange={(e) => updateScore(team.id, level.id, parseInt(e.target.value) || 0)}
                          className="score-input"
                        />
                      </td>
                    ))}
                    <td className="total-cell">{team.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick Payout Buttons */}
      <div className="payout-section">
        <h3>Quick Payout QR Codes</h3>
        <p className="payout-note">Generate QR codes for winners/losers to scan</p>
        <div className="payout-grid">
          {PAYOUTS.map(payout => (
            <button
              key={payout.place}
              className={`payout-btn ${payout.amount >= 0 ? 'bonus' : 'penalty'}`}
              onClick={() => generatePayoutQR(payout.amount, `${payout.place} Place`)}
            >
              <span className="place">{payout.place}</span>
              <span className="amount">
                {payout.amount >= 0 ? '+' : ''}{payout.amount.toLocaleString()}
              </span>
            </button>
          ))}
        </div>
        <div className="special-bonuses">
          <h4>Special Bonuses</h4>
          <div className="button-row">
            <button className="btn bonus" onClick={() => generatePayoutQR(5000, 'Hole-in-One')}>
              Hole-in-One +$5K
            </button>
            <button className="btn bonus" onClick={() => generatePayoutQR(2000, 'Level Master')}>
              Level Master +$2K
            </button>
            <button className="btn bonus" onClick={() => generatePayoutQR(1500, 'Consistent Team')}>
              Consistent +$1.5K
            </button>
            <button className="btn bonus" onClick={() => generatePayoutQR(2000, 'Clutch Finish')}>
              Clutch Finish +$2K
            </button>
          </div>
        </div>

        <div className="sideboard-animations">
          <h4>Sideboard Animations</h4>
          <div className="button-row">
            <button className="btn gold" onClick={triggerHoleInOne}>
              HOLE IN ONE! (Bowling Strike)
            </button>
            <button className="btn" onClick={triggerScore}>
              Score Flash
            </button>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="instructions-section">
        <h3>How to Run {GAME_NAME}</h3>
        <ol>
          <li><strong>Start game</strong> - This pauses trading and announces to all players</li>
          <li><strong>Form teams</strong> - Organize 4-8 teams of 5 players each</li>
          <li><strong>Level 1</strong> - All obstacles OFF, each team plays</li>
          <li><strong>Level 2</strong> - Windmill + Treadmill activate, each team plays</li>
          <li><strong>Level 3</strong> - ALL obstacles ON, each team plays</li>
          <li><strong>Calculate totals</strong> - Lowest total strokes wins</li>
          <li><strong>Payout</strong> - Generate QR codes for each team to scan</li>
          <li><strong>End game</strong> - This resumes trading</li>
        </ol>
      </div>

      <AdminLEDButtons
        title="LED Scroll Events"
        events={[
          { label: 'Game Start', color: '#00c853', type: 'sprite:dead-cat', duration: 10, eventName: 'DEAD CAT BOUNCE!' },
          { label: 'Level 1', color: '#4caf50', type: 'minigolf:level', duration: 3, eventName: 'LEVEL 1!' },
          { label: 'Level 2', color: '#8bc34a', type: 'minigolf:level', duration: 3, eventName: 'LEVEL 2!' },
          { label: 'Level 3', color: '#ff9800', type: 'minigolf:level', duration: 3, eventName: 'LEVEL 3!' },
          { label: 'Nice Shot!', color: '#4caf50', type: 'minigolf:good', duration: 2, eventName: 'NICE SHOT!' },
          { label: 'Money Shot!', color: '#00c853', type: 'minigolf:money-shot', duration: 2, eventName: 'MONEY SHOT!' },
          { label: 'On Fire!', color: '#ff6d00', type: 'minigolf:on-fire', duration: 3, eventName: 'ON FIRE!' },
          { label: 'Birdie!', color: '#ffd700', type: 'minigolf:birdie', duration: 3, eventName: 'BIRDIE!' },
          { label: 'Eagle!', color: '#ffab00', type: 'minigolf:eagle', duration: 4, eventName: 'EAGLE!' },
          { label: 'HOLE IN ONE!', color: '#ffd700', type: 'minigolf:hole-in-one', duration: 5, eventName: 'HOLE IN ONE!' },
          { label: 'Bank Shot!', color: '#651fff', type: 'minigolf:bank-shot', duration: 3, eventName: 'BANK SHOT!' },
          { label: 'Full Send!', color: '#ff3d00', type: 'minigolf:full-send', duration: 3, eventName: 'FULL SEND!' },
          { label: 'Miss!', color: '#f44336', type: 'minigolf:bad', duration: 2, eventName: 'MISS!' },
          { label: 'Whiff!', color: '#d32f2f', type: 'minigolf:whiff', duration: 2, eventName: 'WHIFF!' },
          { label: 'Shanked!', color: '#b71c1c', type: 'minigolf:shanked', duration: 2, eventName: 'SHANKED!' },
          { label: 'Disaster!', color: '#880e4f', type: 'minigolf:disaster', duration: 3, eventName: 'DISASTER!' },
          { label: 'Score!', color: '#4caf50', type: 'minigolf:score', duration: 2, eventName: 'SCORE!' },
          { label: 'Winner', color: '#4caf50', type: 'game:winner', duration: 5, eventName: 'WINNER!' },
          { label: 'Game End', color: '#607d8b', type: 'minigolf:end', duration: 3, eventName: 'GAME OVER' }
        ]}
      />
    </div>
  );
};

export default AdminMinigolf;
